import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Loader2, Eye, HandHeart, Compass, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api` });

const SECTION_ICONS = { awareness: Eye, acceptance: HandHeart, action: Compass };
const SECTION_ACCENT = {
  awareness: { bg: 'bg-[#2C5530]', border: 'border-[#2C5530]', text: 'text-[#2C5530]' },
  acceptance: { bg: 'bg-[#BC562B]', border: 'border-[#BC562B]', text: 'text-[#BC562B]' },
  action: { bg: 'bg-[#1A2E1D]', border: 'border-[#1A2E1D]', text: 'text-[#1A2E1D]' },
};

const RESPONSE_LABELS = [
  { value: 1, label: 'Almost never' },
  { value: 2, label: 'Rarely' },
  { value: 3, label: 'Sometimes' },
  { value: 4, label: 'Often' },
  { value: 5, label: 'Almost always' },
];

// ---- Intro ---------------------------------------------------------------
const Intro = ({ onStart }) => (
  <section className="bg-[#FAFAF9] py-16 md:py-24" data-testid="a3-intro">
    <div className="container mx-auto px-6 md:px-12 max-w-4xl">
      <p className="text-[#BC562B] text-xs uppercase tracking-[0.35em] mb-6">A³ Performance Assessment</p>
      <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A] mb-8">
        The Mental Skills Behind Your Golf
      </h1>
      <p className="text-lg md:text-xl text-[#57534E] leading-relaxed max-w-2xl mb-10">
        Awareness · Acceptance · Action. Thirty golf-specific behavioural questions that map how you actually play under pressure — not how you'd like to.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {[
          { key: 'awareness', title: 'Awareness', line: 'Step back.', body: 'Recognise what is happening.' },
          { key: 'acceptance', title: 'Acceptance', line: 'Step up.', body: 'Make space for what is happening.' },
          { key: 'action', title: 'Action', line: 'Step in.', body: 'Choose how you respond.' },
        ].map((s) => {
          const Icon = SECTION_ICONS[s.key];
          const c = SECTION_ACCENT[s.key];
          return (
            <div key={s.key} className={`bg-white border-t-4 ${c.border} p-6`} data-testid={`intro-card-${s.key}`}>
              <Icon className={`w-6 h-6 mb-4 ${c.text}`} />
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#A8A29E] mb-2">{s.line}</p>
              <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">{s.title}</h3>
              <p className="text-sm text-[#57534E]">{s.body}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-[#E7E5E4] p-6 md:p-8 mb-10">
        <h2 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A] mb-3">Before you begin</h2>
        <ul className="text-[#57534E] text-sm md:text-base leading-relaxed space-y-2 list-disc pl-5">
          <li>Think about how you <span className="font-semibold text-[#1A1A1A]">actually behave</span> under pressure or when things aren't going your way — not how you'd like to.</li>
          <li>There are no right or wrong answers.</li>
          <li>Takes 6–8 minutes. Your profile is generated immediately at the end.</li>
        </ul>
      </div>

      <button
        onClick={onStart}
        className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] hover:bg-[#9A4520] transition-colors"
        data-testid="a3-start-btn"
      >
        Start the Assessment <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-xs text-[#A8A29E] mt-6 max-w-lg">
        These bands describe the self-reported pattern shown by the assessment, not a clinical diagnosis, ability rating or guarantee of golf performance.
      </p>
    </div>
  </section>
);

// ---- Demographics --------------------------------------------------------
const Demographics = ({ value, onChange, onNext, onBack }) => {
  const canProceed = value.name.trim() && /@/.test(value.email);
  return (
    <section className="bg-[#FAFAF9] py-16 min-h-[70vh]" data-testid="a3-demographics">
      <div className="container mx-auto px-6 md:px-12 max-w-2xl">
        <p className="text-[#BC562B] text-xs uppercase tracking-[0.35em] mb-4">A³ · Step 1 of 4</p>
        <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">A little about you</h2>
        <p className="text-[#57534E] mb-8">So we can send your profile and track it over time if you take it again.</p>

        <div className="bg-white border border-[#E7E5E4] p-6 md:p-8 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#57534E] font-semibold mb-1.5">Name <span className="text-[#BC562B]">*</span></label>
            <input type="text" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} className="w-full" data-testid="a3-dem-name" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#57534E] font-semibold mb-1.5">Email <span className="text-[#BC562B]">*</span></label>
            <input type="email" value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })} className="w-full" data-testid="a3-dem-email" />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#57534E] font-semibold mb-1.5">Handicap</label>
              <input type="text" value={value.handicap} onChange={(e) => onChange({ ...value, handicap: e.target.value })} placeholder="e.g. 12" className="w-full" data-testid="a3-dem-handicap" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#57534E] font-semibold mb-1.5">Age range</label>
              <select value={value.age_range} onChange={(e) => onChange({ ...value, age_range: e.target.value })} className="w-full" data-testid="a3-dem-age">
                <option value="">Select…</option>
                <option>Under 18</option><option>18–29</option><option>30–39</option><option>40–49</option><option>50–59</option><option>60+</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#57534E] font-semibold mb-1.5">Playing level</label>
              <select value={value.playing_level} onChange={(e) => onChange({ ...value, playing_level: e.target.value })} className="w-full" data-testid="a3-dem-level">
                <option value="">Select…</option>
                <option>Beginner</option><option>Club golfer</option><option>Low handicap</option><option>Scratch / Elite amateur</option><option>Professional</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#57534E] font-semibold mb-1.5">Play</label>
              <select value={value.competitive} onChange={(e) => onChange({ ...value, competitive: e.target.value })} className="w-full" data-testid="a3-dem-comp">
                <option value="">Select…</option>
                <option>Competitive</option><option>Recreational</option><option>Both</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#57534E] font-semibold mb-1.5">Anything you'd like Scott to know? (optional)</label>
            <textarea rows={3} value={value.comments} onChange={(e) => onChange({ ...value, comments: e.target.value })} className="w-full" data-testid="a3-dem-comments" />
          </div>
        </div>

        <div className="flex justify-between items-center mt-6">
          <button onClick={onBack} className="text-sm text-[#57534E] hover:text-[#1A1A1A] inline-flex items-center gap-1" data-testid="a3-dem-back"><ArrowLeft className="w-4 h-4" /> Back</button>
          <button onClick={onNext} disabled={!canProceed} className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] disabled:opacity-50 transition-colors" data-testid="a3-dem-next">Begin Section 1 <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </section>
  );
};

// ---- Question Section ----------------------------------------------------
const SectionForm = ({ section, sectionIndex, totalSections, answers, onAnswer, onNext, onBack }) => {
  const c = SECTION_ACCENT[section.key];
  const Icon = SECTION_ICONS[section.key];
  const answered = section.questions.filter((q) => answers[q.code]).length;
  const complete = answered === section.questions.length;
  return (
    <section className="bg-[#FAFAF9] py-16 min-h-[70vh]" data-testid={`a3-section-${section.key}`}>
      <div className="container mx-auto px-6 md:px-12 max-w-3xl">
        <p className={`text-xs uppercase tracking-[0.35em] mb-4 ${c.text}`}>A³ · Step {sectionIndex + 2} of {totalSections + 1} · {section.label}</p>
        <div className="flex items-start gap-3 mb-2">
          <Icon className={`w-8 h-8 mt-1 ${c.text}`} />
          <div>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A]">{section.label}</h2>
            <p className="text-[#57534E] text-sm md:text-base mt-1">{section.principle}</p>
          </div>
        </div>
        <p className="text-xs text-[#A8A29E] mb-8">{answered} of {section.questions.length} answered</p>

        <ul className="space-y-4">
          {section.questions.map((q, qi) => (
            <li key={q.code} className="bg-white border border-[#E7E5E4] p-5" data-testid={`a3-q-${q.code}`}>
              <p className="text-sm text-[#57534E] uppercase tracking-wider mb-1">Q{qi + 1}</p>
              <p className="text-base text-[#1A1A1A] mb-4 leading-relaxed">{q.text}</p>
              <div className="grid grid-cols-5 gap-2">
                {RESPONSE_LABELS.map((r) => {
                  const active = answers[q.code] === r.value;
                  return (
                    <button
                      key={r.value}
                      onClick={() => onAnswer(q.code, r.value)}
                      className={`p-2.5 md:p-3 text-center border transition-colors text-xs md:text-sm ${
                        active ? `${c.bg} text-white border-transparent font-semibold` : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#A8A29E]'
                      }`}
                      data-testid={`a3-a-${q.code}-${r.value}`}
                    >
                      <div className="font-bold text-base md:text-lg">{r.value}</div>
                      <div className="text-[10px] md:text-[11px] leading-tight mt-0.5 opacity-90">{r.label}</div>
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>

        <div className="flex justify-between items-center mt-8 sticky bottom-0 bg-[#FAFAF9] py-4 border-t border-[#E7E5E4]">
          <button onClick={onBack} className="text-sm text-[#57534E] hover:text-[#1A1A1A] inline-flex items-center gap-1" data-testid={`a3-section-back-${section.key}`}><ArrowLeft className="w-4 h-4" /> Back</button>
          <button
            onClick={onNext}
            disabled={!complete}
            className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] disabled:opacity-50 transition-colors"
            data-testid={`a3-section-next-${section.key}`}
          >
            {sectionIndex === totalSections - 1 ? 'See my profile' : 'Continue'} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ---- Main flow -----------------------------------------------------------
const A3AssessmentPage = () => {
  const navigate = useNavigate();
  const [definition, setDefinition] = useState(null);
  const [phase, setPhase] = useState('intro'); // intro | demographics | section-0 | section-1 | section-2 | submitting
  const [demographics, setDemographics] = useState({ name: '', email: '', handicap: '', age_range: '', playing_level: '', competitive: '', comments: '' });
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    api.get('/assessment/a3/definition').then(({ data }) => setDefinition(data)).catch(() => toast.error('Could not load assessment.'));
  }, []);

  // Prefill demographics from logged-in user if present
  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        setDemographics((d) => ({ ...d, name: d.name || u.full_name || '', email: d.email || u.email || '' }));
      }
    } catch { /* noop */ }
  }, []);

  const onAnswer = (code, v) => setAnswers((a) => ({ ...a, [code]: v }));

  const submit = async () => {
    setPhase('submitting');
    try {
      const { data } = await api.post('/assessment/a3/submit', { demographics, answers });
      navigate(`/a3-assessment/result/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong.');
      setPhase('section-2');
    }
  };

  if (!definition) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#BC562B]" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="a3-page">
      <Header />
      {phase === 'intro' && <Intro onStart={() => setPhase('demographics')} />}
      {phase === 'demographics' && (
        <Demographics value={demographics} onChange={setDemographics} onNext={() => setPhase('section-0')} onBack={() => setPhase('intro')} />
      )}
      {['section-0', 'section-1', 'section-2'].map((p, idx) => (
        phase === p && (
          <SectionForm
            key={p}
            section={definition.sections[idx]}
            sectionIndex={idx}
            totalSections={definition.sections.length}
            answers={answers}
            onAnswer={onAnswer}
            onBack={() => setPhase(idx === 0 ? 'demographics' : `section-${idx - 1}`)}
            onNext={() => {
              if (idx < definition.sections.length - 1) {
                setPhase(`section-${idx + 1}`);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                submit();
              }
            }}
          />
        )
      ))}
      {phase === 'submitting' && (
        <section className="min-h-[70vh] flex items-center justify-center" data-testid="a3-submitting">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#BC562B] mx-auto mb-4" />
            <p className="text-[#57534E]">Building your A³ profile…</p>
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
};

export default A3AssessmentPage;
